/**
 * <fk-blocklist> — generic grid/carousel controller for sections that list
 * sibling blocks (logos, collection tiles, blog posts, showcase, promo,
 * banners, reviews...). Layout (grid vs carousel, per breakpoint) is decided
 * entirely by CSS classes set from Liquid; this controller only drives the
 * interactions that exist in the DOM, so it's a no-op wherever the track
 * isn't actually scrollable (e.g. currently in grid mode).
 */
(function () {
  if (customElements.get('fk-blocklist')) return;

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class FkBlocklist extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-fk-track]');
      if (!this.track) return;

      this.prevBtn = this.querySelector('[data-fk-prev]');
      this.nextBtn = this.querySelector('[data-fk-next]');
      this.dotsWrap = this.querySelector('[data-fk-dots]');
      this.dragging = false;

      this.boundOnResize = this.onResize.bind(this);
      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      window.addEventListener('resize', this.boundOnResize);
      this.track.addEventListener('pointerdown', this.onDragStart.bind(this));
      this.track.addEventListener('click', (event) => {
        if (this.didDrag) {
          event.preventDefault();
          event.stopPropagation();
          this.didDrag = false;
          return;
        }

        var cardLink = event.target.closest('a.fk-card');
        if (!cardLink || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        window.location.assign(cardLink.href);
      }, true);
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
      this.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
      this.addEventListener('touchend', () => this.scheduleAutoplay(), { passive: true });

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByPage(1));

      this.buildDots();
      this.updateArrows();
      this.startAutoplay();
    }

    disconnectedCallback() {
      if (this.boundOnResize) window.removeEventListener('resize', this.boundOnResize);
      this.stopAutoplay();
      window.clearTimeout(this.autoplayResumeTimer);
    }

    scrollByPage(direction) {
      this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: 'smooth' });
      this.scheduleAutoplay();
    }

    startAutoplay() {
      if (this.dataset.fkAutoplay !== 'true' || REDUCED_MOTION.matches || this.autoplayTimer) return;
      var delay = Number(this.dataset.fkAutoplaySpeed) || 4000;
      this.autoplayTimer = window.setInterval(() => this.advanceAutoplay(), delay);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    scheduleAutoplay() {
      this.stopAutoplay();
      window.clearTimeout(this.autoplayResumeTimer);
      this.autoplayResumeTimer = window.setTimeout(() => this.startAutoplay(), 1200);
    }

    advanceAutoplay() {
      var max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 1) return;
      var atEnd = this.track.scrollLeft >= max - 2;
      this.track.scrollTo({
        left: atEnd ? 0 : Math.min(this.track.scrollLeft + this.track.clientWidth, max),
        behavior: 'smooth'
      });
    }

    onScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.updateArrows();
        this.updateActiveDot();
        this.scrollRaf = null;
      });
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.buildDots();
        this.updateArrows();
      }, 150);
    }

    updateArrows() {
      var max = this.track.scrollWidth - this.track.clientWidth - 1;
      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= max;
    }

    pageCount() {
      if (!this.track.clientWidth) return 1;
      return Math.max(1, Math.round(this.track.scrollWidth / this.track.clientWidth));
    }

    buildDots() {
      if (!this.dotsWrap) return;
      var pages = this.pageCount();
      this.dotsWrap.innerHTML = '';
      if (pages <= 1) return;

      for (var i = 0; i < pages; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'fk-blocklist__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'Slide ' + (i + 1));
        if (i === 0) dot.classList.add('is-active');
        dot.addEventListener(
          'click',
          ((index) => () => {
            this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
            this.scheduleAutoplay();
          })(i)
        );
        this.dotsWrap.appendChild(dot);
      }
    }

    updateActiveDot() {
      if (!this.dotsWrap || !this.track.clientWidth) return;
      var dots = this.dotsWrap.children;
      if (!dots.length) return;
      var activeIndex = Math.round(this.track.scrollLeft / this.track.clientWidth);
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === activeIndex);
      }
    }

    onDragStart(event) {
      if (event.pointerType === 'touch') return;
      /* Links must keep their native click behavior. Drag can start from the
         empty track/gaps; touch users still retain native swipe scrolling. */
      if (event.target.closest('a, button, input, select, textarea')) {
        this.didDrag = false;
        return;
      }
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.track.scrollLeft;
      this.dragging = true;
      this.didDrag = false;

      var onMove = (moveEvent) => {
        if (!this.dragging) return;
        if (!this.didDrag && Math.abs(moveEvent.clientX - this.dragStartX) > 5) {
          this.didDrag = true;
          this.track.classList.add('is-dragging');
          this.track.setPointerCapture(event.pointerId);
        }
        if (!this.didDrag) return;
        this.track.scrollLeft = this.dragStartScroll - (moveEvent.clientX - this.dragStartX);
      };
      var onEnd = () => {
        this.dragging = false;
        this.track.classList.remove('is-dragging');
        if (this.track.hasPointerCapture(event.pointerId)) {
          this.track.releasePointerCapture(event.pointerId);
        }
        this.track.removeEventListener('pointermove', onMove);
        this.track.removeEventListener('pointerup', onEnd);
        this.track.removeEventListener('pointercancel', onEnd);
        this.scheduleAutoplay();
      };

      this.track.addEventListener('pointermove', onMove);
      this.track.addEventListener('pointerup', onEnd);
      this.track.addEventListener('pointercancel', onEnd);
    }
  }

  customElements.define('fk-blocklist', FkBlocklist);
})();
